import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import slugify from 'slugify';

// models
import { IGoal } from '../models/user-goal.model';
import { IGoalSkill } from '../models/goal-skill.model';
import { ISkill } from '../models/skill.model';
import { IGoalSignatureBase } from '../models/goal-signature.model';

// services
import { AuthService } from './auth.service';
import { GoalServiceData } from './goal.service.data';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root'
})
export class GoalService {
  endpoint = '/api/Goals/';
  initialising = false;
  private isLoggedIn = false;

  private readonly customTrackName = 'Custom';
  private readonly HAS_GOALS_CACHE_KEY_PREFIX = 'hasGoals_';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private goalServiceData: GoalServiceData,
    private router: Router
  ) {
    this.isLoggedIn = authService.isLoggedIn();

    // Subscribe to login changes to update state
    this.authService.isLoggedInChange().subscribe({
      next: () => {
        this.isLoggedIn = authService.isLoggedIn();
        // Try to initialize - will check route and auth state
        this.initialiseIfNeeded();
      },
      error: (error) => console.error(`*** GoalService: isLoggedInChange stream error: ${error}`)
    });

    // Subscribe to router navigation to initialize when navigation completes
    // This ensures router.url is available before checking route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe({
      next: () => {
        // Try to initialize after navigation completes this handles cases where login happened before router was ready
        this.initialiseIfNeeded();
      },
      error: (error) => console.error(`*** GoalService: router navigation events stream error: ${error}`)
    });

    // If already logged in, try to initialize (router might not be ready yet, but navigation subscription will handle it)
    if (this.isLoggedIn) {
      this.initialiseIfNeeded();
    }
  }

  // Public method to initialize goals. Should be called by components after checking
  // login status and route. This ensures HTTP calls only happen when auth is ready.
  public initialiseIfNeeded(): void {
    if (this.initialising || this.goalServiceData.initialised) {
      return; // Already initializing or initialized
    }

    if (!this.authService.isLoggedIn()) {
      return; // Not logged in
    }

    // Check if we're on landing page - don't initialize if so
    // Use router.url if available, otherwise allow initialization (router might not be ready yet)
    const path = this.router.url;
    if (path && (path === '/' || path === '/landing')) {
      return; // On landing page, don't initialize
    }

    // If router.url is empty, we might be on initial load - allow initialization
    // The router navigation subscription will handle re-checking after navigation
    this.initialiseGoals();
  }

  public getIsLoggedIn(): boolean {
    return this.isLoggedIn;
  }

  public isInitialised(): boolean {
    return this.goalServiceData.initialised;
  }

  public isInitialising(): boolean {
    return this.initialising;
  }

  public getUncategorisedGoalSkills(): Observable<IGoalSkill[]> {
    const url = this.endpoint + 'GetUncategorisedGoalSkills';
    return this.http.get<IGoalSkill[]>(url, httpOptions);
  }

  getCategories(): Observable<string[]> {
    const url = this.endpoint + 'GetCategories';
    return this.http.get<string[]>(url, httpOptions);
  }

  getEditableSkills(): Observable<ISkill[]> {
    const url = this.endpoint + 'GetSkills';
    return this.http.get<ISkill[]>(url, httpOptions);
  }

  getCategoryIcon(category: string): Observable<string> {
    const sluggedCategory = slugify(category);
    const url = this.endpoint + `GetCategoryIcon/${sluggedCategory}`;
    return this.http.get<string>(url, httpOptions);
  }

  // Get the cached hasGoals value from localStorage, keyed by user ID
  // Public method for guards to check cache instantly
  public getHasGoalsFromCache(): boolean | null {
    if (typeof window === 'undefined') return null;

    const userId = this.authService.userId;
    if (!userId) return null;

    try {
      const cached = localStorage.getItem(`${this.HAS_GOALS_CACHE_KEY_PREFIX}${userId}`);
      return cached === null ? null : cached === 'true';
    } catch {
      return null;
    }
  }

  // Set the cached hasGoals value in localStorage, keyed by user ID
  private setHasGoalsCache(hasGoals: boolean): void {
    if (typeof window === 'undefined') return;

    const userId = this.authService.userId;
    if (!userId) return;

    try {
      localStorage.setItem(`${this.HAS_GOALS_CACHE_KEY_PREFIX}${userId}`, hasGoals.toString());
    } catch {
      // Ignore localStorage errors (quota exceeded, etc.)
    }
  }

  // Clear the hasGoals cache for the current user
  private clearHasGoalsCache(): void {
    if (typeof window === 'undefined') return;

    const userId = this.authService.userId;
    if (!userId) return;

    try {
      localStorage.removeItem(`${this.HAS_GOALS_CACHE_KEY_PREFIX}${userId}`);
    } catch {
      // Ignore localStorage errors
    }
  }

  // Get the count of goals from the server (lightweight endpoint)
  public getGoalsCount(): Observable<number> {
    const url = this.endpoint + 'GetGoalsCount';
    return this.http.get<number>(url, httpOptions);
  }

  // call off to the API to get goals, and on return set the goalServiceData
  private initialiseGoals() {
    this.isLoggedIn = this.authService.isLoggedIn();
    if (this.isLoggedIn) {
      this.initialiseGoalsFromServer();
    }
  }

  private initialiseGoalsFromServer() {
    if (this.initialising) {
      return;
    }

    this.initialising = true;

    // Fire both requests in parallel - count for fast cache update, full list for service initialization
    this.fetchGoalCountAndUpdateCache();
    this.fetchFullGoalsList();
  }

  private fetchGoalCountAndUpdateCache(): void {
    this.getGoalsCount().subscribe({
      next: (count) => {
        const hasGoals = count > 0;
        this.setHasGoalsCache(hasGoals);

        // If count is 0, clear cache as requested
        if (count === 0) {
          this.clearHasGoalsCache();
        }
      },
      error: (error) => {
        console.error(`*** GoalService: getGoalsCount error: ${error.message}`);
        // Cache update failure is non-critical - full goals fetch will still proceed
      },
    });
  }

  private fetchFullGoalsList(): void {
    const url = this.endpoint + 'GetGoals';
    this.http.get<IGoal[]>(url, httpOptions).subscribe({
      next: (goals) => {
        this.goalServiceData.initialiseGoals(goals);
        this.initialising = false;
      },
      error: (error) => {
        console.error(`*** GoalService: initialiseGoals, error when getting the goals. Error: ${error.message}`);
        this.initialising = false;
      },
    });
  }

  /* User Goal Methods */

  // Get the cached goals. Will throw if not initialised
  // returns the collection of goals
  public getGoals(filterActiveOnly: boolean = false): IGoal[] {
    if (filterActiveOnly) {
      return this.goalServiceData.getActiveGoals();
    }

    return this.goalServiceData.getGoals();
  }

  getGoal(goalId: number | undefined): IGoal | undefined {
    if (!goalId) {
      return undefined;
    }
    return this.goalServiceData.getGoal(goalId);
  }

  getGoalFromServer(goalId: number | undefined): Observable<IGoal> {
    if (!goalId) {
      throw new Error('Goal Id cannot be undefined at this point');
    }

    const url = `${this.endpoint}GetGoal/${goalId}`;
    return this.http.get<IGoal>(url);
  }

  addGoal(goal: IGoal): Observable<IGoal> {
    const url = this.endpoint + 'AddGoal';

    return this.http.post<IGoal>(url, goal, httpOptions).pipe(
      map((resultGoal: IGoal) => {
        if (!resultGoal.selectedTrackName) {
          resultGoal.selectedTrackName = this.customTrackName;
        }
        // Update cache BEFORE adding goal to service data (which emits event)
        // This ensures hasGoals status is updated immediately when first goal is saved
        this.setHasGoalsCache(true);

        // Wrap addGoal in try-catch to handle any errors gracefully
        try {
          const added = this.goalServiceData.addGoal(resultGoal);
          if (!added) {
            console.warn(`*** GoalService: addGoal returned false for goal ${resultGoal.id} - goal may be duplicate`);
          }
        } catch (err) {
          console.error(`*** GoalService: Error adding goal to service data:`, err);
          // Re-throw to ensure the error is handled by the subscriber
          throw err;
        }

        return resultGoal;
      })
    );
  }

  updateGoalAndServiceData(goal: IGoal): Observable<IGoal> {
    const url = this.endpoint + 'UpdateGoal';
    return this.http.put<IGoal>(url, goal, httpOptions).pipe(
      map((resultGoal: IGoal) => {
        if (!resultGoal.selectedTrackName) {
          resultGoal.selectedTrackName = this.customTrackName;
        }
        this.goalServiceData.updateGoal(resultGoal);
        return resultGoal;
      })
    );
  }

  updateGoal(goal: IGoal): Observable<IGoal> {
    const url = this.endpoint + 'UpdateGoal';
    return this.http.put<IGoal>(url, goal, httpOptions);
  }

  updateGoalSkill(goalSkill: IGoalSkill): Observable<IGoal> {
    const url = this.endpoint + 'UpdateGoalSkill';
    return this.http.post<IGoal>(url, goalSkill, httpOptions);
  }

  public toggleGoalCompleted(goal: IGoal): Observable<IGoal> {
    const url = this.endpoint + 'ToggleGoalComplete';
    return this.http.put<IGoal>(url, goal, httpOptions).pipe(
      map((resultGoal: IGoal) => {
        if (!resultGoal.selectedTrackName) {
          resultGoal.selectedTrackName = this.customTrackName;
        }
        console.log(`*** GoalService: toggleGoalCompleted succeeded, setting to: ${resultGoal.dateCompleted === undefined ? 'Active' : 'Completed'}`);
        this.goalServiceData.updateGoal(resultGoal);
        return resultGoal;
      })
    );
  }

  public deleteGoal(goal: IGoal): Observable<IGoal> {
    const url = this.endpoint + 'Delete';

    return this.http.put<IGoal>(url, goal, httpOptions).pipe(
      map((deletedGoal: IGoal) => {
        this.goalServiceData.deleteGoal(deletedGoal);

        // Check if user now has 0 goals - if so, clear cache
        const remainingGoals = this.goalServiceData.getGoals().length;
        if (remainingGoals === 0) {
          this.clearHasGoalsCache();
        }

        return deletedGoal;
      })
    );
  }


  public hasActiveGoals(): boolean {
    return this.goalServiceData.hasActiveGoals;
  }

  // PLEASE: Don't forget to unsubscribe or take(1)
  public getGoalsUpdatedEvent(): Subject<boolean> {
    return this.goalServiceData.getGoalsUpdatedEvent();
  }

  public isGoalDuplicate(inputGoal: IGoal): boolean {
    for (const goal of this.goalServiceData.getGoals()) {
      if (goal.industry === inputGoal.industry && goal.profession === inputGoal.profession && goal.specialisation === inputGoal.specialisation && goal.level === inputGoal.level) {
        return true;
      }
    }
    return false;
  }

  public getSuggestedCategorySkills(industry: string, profession: string, level: string): Observable<ISkill[]> {
    const url = this.endpoint + 'GetSuggestedCategorySkills';
    return this.http.get<ISkill[]>(url, { params: { industry, profession, level } });
  }

  // TODO: Go via skillService
  public updateSkill(skill: ISkill): Observable<ISkill> {
    const url = this.endpoint + 'UpdateSkill';
    return this.http.post<ISkill>(url, skill);
  }

  // TODO: Go via skillService
  public retireSkill(skill: ISkill): Observable<ISkill> {
    const url = this.endpoint + 'RetireSkill';
    return this.http.post<ISkill>(url, skill.id);
  }

  public getGoalsForPlaylist(playlistId: number): Observable<IGoal[]> {
    const url = this.endpoint + `GetGoalsForPlaylist/`;
    return this.http.get<IGoal[]>(url, { params: { playlistId: playlistId.toString() } });
  }

  public getGoalsForActivity(activityId: number): Observable<IGoal[]> {
    const url = this.endpoint + `GetGoalsForActivity/`;
    return this.http.get<IGoal[]>(url, { params: { activityId: activityId.toString() } });
  }

  public getGoalSignature(goalId: number): Observable<IGoalSignatureBase> {
    const url = this.endpoint + `GetGoalSignature/`;
    return this.http.get<IGoalSignatureBase>(url, { params: { goalId: goalId.toString() } });
  }

  public getBrokenGoalsCount(): Observable<number> {
    const url = this.endpoint + `GetBrokenGoalsCount/`;
    return this.http.get<number>(url, httpOptions);
  }



  // GetHasGoalsFast - checks cache first for instant response, then verifies with server
  // Returns Observable that emits immediately if cache exists, otherwise fetches from server
  public getHasGoalsFast(): Observable<boolean> {
    // If we're already initialized, just use the in-memory data - no need for network call
    if (this.goalServiceData.initialised) {
      return of(this.getGoals().length > 0);
    }

    // Check cache first for instant response
    const cached = this.getHasGoalsFromCache();
    if (cached !== null) {
      return new Observable(observer => {
        observer.next(cached);
        observer.complete();

        // Still verify in background, but ONLY if we aren't already initializing or initialized
        // If we are initializing, that process will update the cache/state appropriately
        if (!this.initialising && !this.goalServiceData.initialised) {
          this.getGoalsCount().subscribe({
            next: (count) => {
              const hasGoals = count > 0;
              if (hasGoals !== cached) {
                this.setHasGoalsCache(hasGoals);
                if (count === 0) {
                  this.clearHasGoalsCache();
                }
              }
            },
            error: (error) => {
            // Don't need to do much if there are errors - cache is a good enough fallback
              console.error(`*** Failed to getGoalsCount: ${error.message}`);
            }
          });
        }
      });
    }

    // No cache - get from server
    // If already initializing, maybe we should just return false/unknown? But consumer expects observable.
    // We let this proceed as normal if no cache exists.
    return this.getGoalsCount().pipe(
      map(count => {
        const hasGoals = count > 0;
        this.setHasGoalsCache(hasGoals);
        if (count === 0) {
          this.clearHasGoalsCache();
        }
        return hasGoals;
      })
    );
  }
}
